import React, { useEffect, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ContentContainer from "components/Container";
import { BiPaperPlane } from "react-icons/bi";
import { Box, Button, Center, Icon, Input, Text, useToast } from "@chakra-ui/react";
import { FullPageSpinner } from "components/Spinner";
import useSendEmailVerificationCode from "api/accounts/useSendEmailVerificationCode";
import useVerifyEmailVerificationCode from "api/accounts/useVerifyEmailVerificationCode";
import { parseVerificationErrorCodeMessage } from "api/accounts/utils";

const EmailVerificationContent = ({ email, initialNonce, onClose, onSuccess }) => {
  const { formatMessage } = useIntl();
  const [code, setCode] = useState("");
  const [nonce, setNonce] = useState(initialNonce);

  const toast = useToast();

  // resend email verification hook
  const {
    mutateAsync: resendEmailVerificationCode,
    isSuccess: isResendEmailVerificationCodeSuccess,
    isPending: isResendingEmailVerificationCode,
    error: resendEmailVerificationCodeError,
    data: resendEmailVerificationCodeData,
  } = useSendEmailVerificationCode();

  // verify email verification hook
  const {
    mutateAsync: verifyEmailVerificationCode,
    isSuccess: isVerifyEmailVerificationCodeSuccess,
    isPending: isVerifyingEmailVerificationCode,
    error: verifyEmailVerificationCodeError,
  } = useVerifyEmailVerificationCode();

  // encapsulate function to resend email verification code
  const callResendEmailVerificationCode = useCallback(() => {
    resendEmailVerificationCode({ email });
    setCode("");
  }, [resendEmailVerificationCode, email, setCode]);

  // encapsulated function to verify email verification code
  const callVerifyEmailVerificationCode = useCallback(() => {
    verifyEmailVerificationCode({ code, nonce });
  }, [verifyEmailVerificationCode, code, nonce]);

  // if resend email was sent successfully, store the nonce
  useEffect(() => {
    if (isResendEmailVerificationCodeSuccess) {
      setNonce(resendEmailVerificationCodeData.payload.nonce);
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Pop up title",
        }),
        description: formatMessage({
          defaultMessage: "Email verification code sent successfully",
          description: "Pop up message",
        }),
        status: "success",
        isClosable: true,
      });
    }
  }, [
    formatMessage,
    isResendEmailVerificationCodeSuccess,
    resendEmailVerificationCodeData,
    setNonce,
    toast,
  ]);

  // if code is of length 6, call verify email verification code
  useEffect(() => {
    if (code.length === 6) {
      callVerifyEmailVerificationCode();
    }
  }, [code, callVerifyEmailVerificationCode, onSuccess, onClose]);

  // if code was verified successfully, call onSuccess with success toast
  useEffect(() => {
    if (isVerifyEmailVerificationCodeSuccess) {
      onSuccess();
      onClose();
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Pop up title",
        }),
        description: formatMessage({
          defaultMessage: "Your email is updated",
          description: "Pop up message",
        }),
        status: "success",
        isClosable: true,
      });
    }
  }, [isVerifyEmailVerificationCodeSuccess, onSuccess, onClose, toast, formatMessage]);

  // display any error in toast
  useEffect(() => {
    const error = resendEmailVerificationCodeError || verifyEmailVerificationCodeError;
    if (error) {
      toast({
        title: "Error",
        description: parseVerificationErrorCodeMessage(error.cause) || error.message,
        status: "error",
        isClosable: true,
      });
    }
  }, [resendEmailVerificationCodeError, verifyEmailVerificationCodeError, toast]);

  const isDisabled = isResendingEmailVerificationCode || isVerifyingEmailVerificationCode;

  if (isVerifyingEmailVerificationCode) {
    return <FullPageSpinner />;
  }
  return (
    <ContentContainer mt="16" fullPage>
      <Center>
        <Icon as={BiPaperPlane} w="16" h="16" color="integrator.500" />
      </Center>
      <Center mt="8">
        <Text fontSize="lg" fontWeight="500" color="primaryText">
          <FormattedMessage defaultMessage="Verify your email" description="Header title" />
        </Text>
      </Center>
      <Box mt="16">
        <Text color="primaryText">
          <FormattedMessage
            defaultMessage="We have sent a verification code to {email}. Please check your email and enter the code that you received."
            description="Informative message"
            values={{ email }}
          />
        </Text>
      </Box>
      <Box mt="10">
        <Input
          role="email-code-input" // eslint-disable-line jsx-a11y/aria-role
          placeholder={formatMessage({
            defaultMessage: "Enter verification code",
            description: "Input field placeholder",
          })}
          size="lg"
          value={code}
          maxLength={6}
          sx={{ letterSpacing: code ? "0.5rem" : "normal" }} // so placeholder doesn't have letter spacing
          isDisabled={isDisabled}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
      </Box>
      <Box mt="12">
        <Button colorScheme="gray" w="100%" onClick={onClose}>
          <FormattedMessage defaultMessage="Cancel" description="Button label" />
        </Button>
      </Box>
      <Box mt="5">
        <Text color="primaryText">
          <FormattedMessage
            defaultMessage="Didn't get a code? <click>Click to resend</click>"
            description=""
            values={{
              click: (msg) => (
                <Text
                  role="resend-email-code" // eslint-disable-line jsx-a11y/aria-role
                  as="span"
                  color="integrator.400"
                  cursor="pointer"
                  onClick={isDisabled ? null : callResendEmailVerificationCode}
                >
                  {msg}
                </Text>
              ),
            }}
          />
        </Text>
      </Box>
      <Box mt="auto">
        <Text as="i" color="primaryText">
          <FormattedMessage
            defaultMessage="Make sure to keep this window open while you check for the verification code."
            description="Informative message"
          />
        </Text>
      </Box>
    </ContentContainer>
  );
};

export default EmailVerificationContent;
